/**
 * Per-endpoint write policies, declared by features next to their api modules.
 *
 * No policy means `online-only`: the write goes to the network exactly as it
 * does today and is never queued. A policy says which entity a write touches
 * (the outbox is FIFO per entity), how to echo it into cached resources so
 * reads reflect queued intent, how revisions travel, and what to do on a 409.
 */

import type { WriteMethod, WriteScope } from './store';
import type { ApiError } from '@shared/services/requestTypes';

export interface WriteRequest {
  method: WriteMethod;
  url: string;
  body: unknown;
}

/** The slice of the store a policy may touch while echoing a write, inside one transaction. */
export interface LocalStore {
  get<T>(key: string): Promise<T | null>;
  put<T>(key: string, data: T): Promise<void>;
  delete(key: string): Promise<void>;
}

export interface RevisionAdapter {
  /** The revision a successful response carries, or null when it carries none. */
  fromResponse: (data: unknown) => string | null;
  /** The revision the cached copy of the entity carries, used when no reply has been threaded. */
  fromCache?: (store: LocalStore, req: WriteRequest) => Promise<string | null>;
  /** The body with the feature's `expected_revision` set; null means "no check". */
  withExpected: (body: unknown, revision: string | null) => unknown;
  /** Whether a successful reply's revision becomes the next queued row's expectation. Default true. */
  threadForward?: boolean;
}

/**
 * What a conflict handler settled on. `resolved`: the row is done and the
 * framework announces its touched keys as acknowledged. `resolved-announced`:
 * the row is done and the handler has already told the screens what happened
 * (a copy made, a key discarded), so the framework must not announce for it
 * or a listener would hear "your save landed" about text the server refused.
 * `failed`: the row stays, marked failed.
 */
export type ConflictOutcome = 'resolved' | 'resolved-announced' | 'failed';

export interface WritePolicy {
  replay: 'idempotent';
  /** The row this write touches. Rows for one entity replay in order; entities are independent. */
  entity: (req: WriteRequest) => string;
  /** A later queued write with the same method and path replaces the earlier queued one. */
  coalesce?: 'last-wins';
  /** True for a write that removes the entity: it drops every queued row for it. */
  supersedes?: boolean;
  /** True for the write that creates the entity; rows queued behind it carry no expected revision until it is acknowledged. */
  createsEntity?: boolean;
  /** Human words for the pending-changes sheet. */
  describe?: (req: WriteRequest) => string;
  /**
   * Update the cached resources this write affects. Must be idempotent: it is
   * re-applied after refetches. Store the reply's revision in the cached copy
   * so `fromCache` can find it.
   *
   * Must await only the `LocalStore` methods it is handed. It runs inside the
   * caller's IndexedDB transaction, and awaiting anything else lets that
   * transaction auto-commit underneath it, so a later failure can no longer
   * be rolled back.
   */
  applyLocally?: (store: LocalStore, req: WriteRequest, reply?: unknown) => Promise<void>;
  /** The response the caller should see when the write was only queued. */
  echo?: (req: WriteRequest) => unknown;
  /** Resource keys this write touches, so a discard can drop them for a refetch and an ack can announce them. */
  touches?: (req: WriteRequest) => string[];
  /** HTTP statuses that count as acknowledged on replay (a delete's 404: the row is already gone). */
  acceptStatus?: (status: number) => boolean;
  /**
   * Reverse this write's echo when the row is discarded or cancelled, without
   * dropping cached copies the echo merely edited. Defaults to deleting every
   * key in `touches`, which is right for a write that changed one cached copy
   * and wrong for one that added a row to a cached list. Same transaction
   * contract as `applyLocally`: await only the `LocalStore` it is handed.
   */
  undoLocally?: (store: LocalStore, req: WriteRequest) => Promise<void>;
  revision?: RevisionAdapter;
  /**
   * Runs for a 409, on replay or on a direct online write, outside every
   * entity lock. On replay the flusher still holds the flush lock, so a
   * handler may call `flush()` (it only requests a re-drain) but must never
   * await `wipeOfflineStore()`. Store access must use `scope`.
   */
  onConflict?: (req: WriteRequest, err: ApiError, scope: WriteScope) => Promise<ConflictOutcome>;
}

type Matcher = string | RegExp;

const registry: { method: WriteMethod; matcher: Matcher; policy: WritePolicy }[] = [];

/** Path without query or fragment. */
function pathOf(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

/**
 * Declare how writes to an endpoint behave when offline.
 *
 * A `RegExp` matcher must carry neither the `g` nor the `y` flag: those make
 * `test` stateful through `lastIndex`, so the same url would match on one
 * lookup and miss on the next.
 */
export function registerWritePolicy(
  method: WriteMethod,
  matcher: Matcher,
  policy: WritePolicy
): void {
  registry.push({ method, matcher, policy });
}

/** The policy for a request, or null for online-only. The most recent match wins. */
export function writePolicyFor(req: WriteRequest): WritePolicy | null {
  const path = pathOf(req.url);
  for (let i = registry.length - 1; i >= 0; i -= 1) {
    const { method, matcher, policy } = registry[i]!;
    if (method !== req.method) continue;
    if (typeof matcher === 'string' ? matcher === path : matcher.test(path)) return policy;
  }
  return null;
}

/** Tests register per case. */
export function resetWritePoliciesForTests(): void {
  registry.length = 0;
}
